import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Search, UserRoundPlus } from "lucide-react";
import { socialErrorMessage, type SocialClient } from "../../hooks/useSocial";
import type { PublicProfile } from "../../types/protocol";
import { Avatar } from "../ui/Avatar";

const DEBOUNCE = 300;
const MIN_LENGTH = 2;

interface FriendSearchProps {
  social: SocialClient;
}

export const FriendSearch = ({ social }: FriendSearchProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PublicProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [invited, setInvited] = useState<string[]>([]);
  const generation = useRef(0);
  const { search, requestFriend } = social;

  const trimmed = query.trim();

  useEffect(() => {
    if (trimmed.length < MIN_LENGTH) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const attempt = ++generation.current;
    const timer = window.setTimeout(() => {
      void search(trimmed).then((found) => {
        // Une frappe plus récente a déjà relancé la recherche : on l'ignore.
        if (attempt !== generation.current) return;
        setResults(found);
        setSearching(false);
      });
    }, DEBOUNCE);

    return () => window.clearTimeout(timer);
  }, [search, trimmed]);

  const knownIds = useMemo(() => {
    const friends = new Set(social.friends.map((friend) => friend.userId));
    const pending = new Set(
      [...social.incoming, ...social.outgoing].map((request) => request.profile.userId),
    );
    return { friends, pending };
  }, [social.friends, social.incoming, social.outgoing]);

  const add = async (profile: PublicProfile) => {
    const ok = await requestFriend(profile.handle);
    if (ok) setInvited((current) => [...current, profile.userId]);
  };

  const failure = socialErrorMessage(social.error);
  const selfId = social.snapshot?.self.userId;

  return (
    <div className="friend-search">
      <label className="field-label" htmlFor="friend-search-input">
        Cherche un joueur par pseudo
      </label>
      <div className="text-field">
        <span className="text-field__sigil" aria-hidden="true"><Search size={17} /></span>
        <input
          id="friend-search-input"
          type="search"
          autoComplete="off"
          spellCheck={false}
          maxLength={32}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Camille ou Camille#0421"
        />
        {searching && <span className="button-spinner friend-search__spinner" aria-hidden="true" />}
      </div>
      <p className="social-hint">
        Deux caractères suffisent. Ajoute les quatre chiffres pour viser une personne précise.
      </p>

      {failure && social.pending === null && (
        <p className="social-alert" role="alert">{failure}</p>
      )}

      <ul className="social-list" aria-live="polite">
        <AnimatePresence initial={false}>
          {results.map((profile) => {
            const alreadyFriend = knownIds.friends.has(profile.userId);
            const alreadyAsked = invited.includes(profile.userId) || knownIds.pending.has(profile.userId);
            const isSelf = profile.userId === selfId;
            return (
              <motion.li
                className="friend-row"
                key={profile.userId}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
              >
                <Avatar profile={profile} size="md" />
                <span className="friend-row__identity">
                  <strong>{profile.displayName}</strong>
                  <small>#{profile.discriminator}</small>
                </span>
                <span className="friend-row__actions">
                  {isSelf ? (
                    <span className="friend-row__note">C’est toi</span>
                  ) : alreadyFriend ? (
                    <span className="friend-row__note"><Check size={13} /> Déjà ami</span>
                  ) : alreadyAsked ? (
                    <span className="friend-row__note"><Check size={13} /> Demande envoyée</span>
                  ) : (
                    <button
                      className="primary-button primary-button--small"
                      type="button"
                      onClick={() => void add(profile)}
                      disabled={social.pending === "request"}
                      aria-label={`Ajouter ${profile.handle} en ami`}
                    >
                      {social.pending === "request" ? (
                        <span className="button-spinner" aria-hidden="true" />
                      ) : (
                        <UserRoundPlus size={15} />
                      )}
                      Ajouter
                    </button>
                  )}
                </span>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>

      {trimmed.length >= MIN_LENGTH && !searching && results.length === 0 && (
        <div className="social-empty">
          <span className="social-empty__icon" aria-hidden="true"><Search size={20} /></span>
          <strong>Personne à ce nom</strong>
          <p>Vérifie l’orthographe, ou demande son identifiant complet du type <b>Camille#0421</b>.</p>
        </div>
      )}
    </div>
  );
};
